import React, { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color mapping for node kinds
const KIND_COLORS = {
  Anchor: "#6366f1",      // Indigo
  Cell: "#22c55e",        // Green
  Cytokine: "#f97316",    // Orange
  Chemokine: "#eab308",   // Yellow
  PRR: "#a855f7",         // Purple
  Complement: "#14b8a6",  // Teal
  Antibody: "#ec4899",    // Pink
  MHC: "#06b6d4",         // Cyan
  Effector: "#ef4444",    // Red
  Process: "#8b5cf6",     // Violet
};

const VisitorStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (e) {
        console.error("Failed to fetch stats", e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="p-8 text-center text-white">Loading stats...</div>;
  if (!stats) return <div className="p-8 text-center text-white">Stats not available</div>;

  return (
    <div className="max-w-4xl mx-auto p-8 text-gray-800">
      <h1 className="text-2xl font-bold mb-6 text-white">Visitor Statistics</h1>
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <p className="text-4xl font-bold text-indigo-600">{stats.total_visits.toLocaleString()}</p>
        <p className="text-gray-500">Total Visits</p>
      </div>

      <h2 className="text-xl font-semibold mb-4 text-white">Recent Log</h2>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stats.recent_visitors.map((v, i) => (
              <tr key={i}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(v.timestamp).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{v.ip}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.path}</td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={v.user_agent}>{v.user_agent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-8 text-center">
        <button
          onClick={() => window.location.href = '/'}
          className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded text-white border border-white/30 backdrop-blur-sm"
        >
          Back to App
        </button>
      </div>
    </div>
  );
};

// Hidden node kinds (not shown in filter but still rendered)
const HIDDEN_KINDS = ["Transplant", "Cancer"];

// Navigation Tabs
const TabButton = ({ active, onClick, children, icon }) => (
  <button
    onClick={onClick}
    data-testid={`tab-${children.toLowerCase().replace(/\s+/g, '-')}`}
    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${active
      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30 scale-105"
      : "bg-white/80 text-gray-700 hover:bg-white hover:shadow-md"
      }`}
  >
    {icon}
    {children}
  </button>
);

// Interactive Mindmap Component
const MindmapViewer = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [nodeKinds, setNodeKinds] = useState([]);
  const [selectedKinds, setSelectedKinds] = useState([]);
  const [showLabels, setShowLabels] = useState(true);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(true);
  const [isolatedNodes, setIsolatedNodes] = useState([]);
  const containerRef = useRef(null);
  const networkRef = useRef(null);

  // Fetch scenarios and node kinds
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [scenariosRes, kindsRes] = await Promise.all([
          axios.get(`${API}/scenarios`),
          axios.get(`${API}/node-kinds`)
        ]);
        setScenarios(scenariosRes.data.scenarios);
        // Filter out hidden kinds from the list
        const visibleKinds = kindsRes.data.kinds.filter(k => !HIDDEN_KINDS.includes(k));
        setNodeKinds(visibleKinds);
        setSelectedKinds(visibleKinds);
        if (scenariosRes.data.scenarios.length > 0) {
          setSelectedScenario(scenariosRes.data.scenarios[0]);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch graph data when scenario changes
  useEffect(() => {
    const fetchGraph = async () => {
      if (!selectedScenario) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API}/graph/${encodeURIComponent(selectedScenario)}`);
        setGraphData(response.data);
      } catch (error) {
        console.error("Error fetching graph:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGraph();
  }, [selectedScenario]);

  // Initialize vis-network
  useEffect(() => {
    if (!containerRef.current || graphData.nodes.length === 0) return;

    const loadVisNetwork = async () => {
      const vis = await import('vis-network/standalone');

      // Filter nodes by selected kinds
      const filteredNodes = graphData.nodes.filter(n => selectedKinds.includes(n.kind));
      const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
      const filteredEdges = graphData.edges.filter(e =>
        filteredNodeIds.has(e.from) && filteredNodeIds.has(e.to)
      );

      // Track isolated nodes (nodes with no visible edges)
      const nodesWithEdges = new Set();
      filteredEdges.forEach(e => {
        nodesWithEdges.add(e.from);
        nodesWithEdges.add(e.to);
      });
      const isolated = filteredNodes.filter(n => !nodesWithEdges.has(n.id));
      setIsolatedNodes(isolated);

      // Don't render if no nodes to show
      if (filteredNodes.length === 0) {
        if (networkRef.current) {
          networkRef.current.destroy();
          networkRef.current = null;
        }
        return;
      }

      // Assign positions to nodes when there are few or no edges
      const needsManualPosition = filteredEdges.length === 0 || filteredNodes.length <= 5;

      const nodeData = filteredNodes.map((n, index) => {
        const baseNode = {
          id: n.id,
          label: showLabels ? n.id : "",
          title: `<strong>${n.id}</strong><br/>Type: ${n.kind}<br/>${n.notes}`,
          color: {
            background: KIND_COLORS[n.kind] || "#9ca3af",
            border: "#1f2937",
            highlight: {
              background: KIND_COLORS[n.kind] || "#9ca3af",
              border: "#6366f1"
            }
          },
          font: {
            color: "#1f2937",
            size: 10,
            face: "Inter, system-ui, sans-serif"
          },
          shape: n.kind === "Anchor" ? "diamond" : "dot",
          size: n.kind === "Anchor" ? 12 : 9,
          borderWidth: 1,
          shadow: true
        };

        // Add manual positions for isolated nodes
        if (needsManualPosition) {
          const angle = (2 * Math.PI * index) / filteredNodes.length;
          const radius = 200;
          baseNode.x = Math.cos(angle) * radius;
          baseNode.y = Math.sin(angle) * radius;
        }

        return baseNode;
      });

      const nodes = new vis.DataSet(nodeData);

      const edges = new vis.DataSet(
        filteredEdges.map(e => ({
          from: e.from,
          to: e.to,
          label: e.rel,
          title: e.notes || e.rel,
          arrows: "to",
          color: {
            color: "#9ca3af",
            highlight: "#6366f1"
          },
          font: {
            size: 10,
            color: "#6b7280",
            strokeWidth: 0,
            align: "middle"
          },
          smooth: {
            type: "curvedCW",
            roundness: 0.2
          }
        }))
      );

      const options = {
        layout: {
          improvedLayout: true,
          hierarchical: false,
          randomSeed: 42
        },
        physics: {
          enabled: true,
          solver: "forceAtlas2Based",
          forceAtlas2Based: {
            gravitationalConstant: filteredEdges.length === 0 ? -100 : -50,
            centralGravity: 0.05,
            springLength: 150,
            springConstant: 0.08,
            avoidOverlap: 1
          },
          stabilization: {
            enabled: true,
            iterations: 100,
            fit: true
          }
        },
        interaction: {
          hover: true,
          tooltipDelay: 200,
          zoomView: true,
          dragView: true,
          dragNodes: true
        }
      };

      if (networkRef.current) {
        networkRef.current.destroy();
      }

      networkRef.current = new vis.Network(
        containerRef.current,
        { nodes, edges },
        options
      );

      // Fix node position when user drags it
      networkRef.current.on('dragEnd', function (params) {
        if (params.nodes.length > 0) {
          const nodeId = params.nodes[0];
          const positions = networkRef.current.getPositions([nodeId]);
          nodes.update({
            id: nodeId,
            x: positions[nodeId].x,
            y: positions[nodeId].y,
            fixed: { x: true, y: true }
          });
        }
      });

      // Force fit view after creation
      networkRef.current.once('afterDrawing', () => {
        networkRef.current.fit();
      });

      // Also fit after a short delay as backup
      setTimeout(() => {
        if (networkRef.current) {
          networkRef.current.fit({
            animation: false
          });
          networkRef.current.redraw();
        }
      }, 500);
    };

    loadVisNetwork();

    return () => {
      if (networkRef.current) {
        networkRef.current.destroy();
      }
    };
  }, [graphData, selectedKinds, showLabels]);

  const toggleKind = (kind) => {
    setSelectedKinds(prev =>
      prev.includes(kind)
        ? prev.filter(k => k !== kind)
        : [...prev, kind]
    );
  };

  const recenterNetwork = () => {
    if (networkRef.current) {
      // Get all node IDs
      const nodeIds = networkRef.current.body.data.nodes.getIds();

      // Unfix all nodes so physics can reposition them
      nodeIds.forEach(nodeId => {
        networkRef.current.body.data.nodes.update({
          id: nodeId,
          fixed: { x: false, y: false }
        });
      });

      // Re-enable physics temporarily to reposition nodes
      networkRef.current.setOptions({ physics: { enabled: true } });

      // After stabilization, fit the view
      networkRef.current.once('stabilized', () => {
        networkRef.current.fit({
          animation: {
            duration: 500,
            easingFunction: "easeInOutQuad"
          }
        });
      });

      // Start stabilization
      networkRef.current.stabilize(100);
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Scenario Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🎯 Select Immune Scenario
            </label>
            <select
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              data-testid="scenario-select"
              className="w-full px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all font-medium text-gray-700"
            >
              {scenarios.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Node Kind Filter */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🔬 Filter by Component Type
            </label>
            <div className="flex flex-wrap gap-2">
              {nodeKinds.filter(kind => !HIDDEN_KINDS.includes(kind)).map(kind => (
                <button
                  key={kind}
                  onClick={() => toggleKind(kind)}
                  data-testid={`kind-filter-${kind.toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${selectedKinds.includes(kind)
                    ? "shadow-md scale-105"
                    : "opacity-50 hover:opacity-75"
                    }`}
                  style={{
                    backgroundColor: selectedKinds.includes(kind)
                      ? KIND_COLORS[kind] || "#9ca3af"
                      : "#e5e7eb",
                    color: selectedKinds.includes(kind) ? "white" : "#6b7280"
                  }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: KIND_COLORS[kind] || "#9ca3af" }}></span>
                  {kind}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Show Labels Toggle */}
        <div className="mt-4 flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
              data-testid="show-labels-toggle"
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">Show Labels</span>
          </label>
        </div>
      </div>

      {/* Graph Container */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="text-white font-bold text-lg">
              {selectedScenario} - Immune Response Network
            </h3>
            <p className="text-indigo-100 text-sm mt-1">
              Drag nodes to reposition & fix in place. Scroll to zoom. Hover for details.
            </p>
          </div>
          <button
            onClick={recenterNetwork}
            data-testid="recenter-btn"
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-all duration-200 hover:scale-105"
            title="Reset all nodes to original positions"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            Recenter
          </button>
        </div>
        {loading ? (
          <div className="h-[600px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          </div>
        ) : (
          <div
            ref={containerRef}
            data-testid="mindmap-container"
            className="h-[600px] w-full"
          />
        )}

        {/* Display isolated nodes that may not render in the graph */}
        {!loading && isolatedNodes.length > 0 && (
          <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-t border-indigo-100">
            <p className="text-sm font-semibold text-indigo-700 mb-3">
              📌 Components without direct connections in current filter ({isolatedNodes.length}):
            </p>
            <div className="flex flex-wrap gap-3">
              {isolatedNodes.map(n => (
                <div
                  key={n.id}
                  className="px-4 py-2 rounded-lg shadow-md flex items-center gap-2 text-white font-medium"
                  style={{ backgroundColor: KIND_COLORS[n.kind] || "#9ca3af" }}
                  title={n.notes}
                >
                  <span className="w-2 h-2 rounded-full bg-white/40"></span>
                  {n.id}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-gray-100">
        <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          📊 Component Legend
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {Object.entries(KIND_COLORS).filter(([kind]) => !HIDDEN_KINDS.includes(kind)).map(([kind, color]) => (
            <div key={kind} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <div
                className={`w-4 h-4 shadow-sm ${kind === "Anchor" || kind === "Process" ? "rounded-sm" : "rounded-full"}`}
                style={{ backgroundColor: color }}
              ></div>
              <span className="text-sm text-gray-700 font-medium">{kind}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Pathway Arrangement Game
const PathwayGame = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [steps, setSteps] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const stepsContainerRef = useRef(null);

  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const response = await axios.get(`${API}/scenarios`);
        setScenarios(response.data.scenarios);
        if (response.data.scenarios.length > 0) {
          setSelectedScenario(response.data.scenarios[0]);
        }
      } catch (error) {
        console.error("Error fetching scenarios:", error);
      }
    };
    fetchScenarios();
  }, []);

  useEffect(() => {
    const loadPathway = async () => {
      if (!selectedScenario) return;
      setLoading(true);
      setResults(null);
      try {
        const response = await axios.get(
          `${API}/pathway/${encodeURIComponent(selectedScenario)}?shuffle=true`
        );
        setSteps(response.data.steps);
      } catch (error) {
        console.error("Error loading pathway:", error);
      } finally {
        setLoading(false);
      }
    };
    loadPathway();
  }, [selectedScenario]);

  const loadPathway = async () => {
    setLoading(true);
    setResults(null);
    try {
      const response = await axios.get(
        `${API}/pathway/${encodeURIComponent(selectedScenario)}?shuffle=true`
      );
      setSteps(response.data.steps);
    } catch (error) {
      console.error("Error loading pathway:", error);
    } finally {
      setLoading(false);
    }
  };

  // Desktop drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newSteps = [...steps];
    const draggedItem = newSteps[draggedIndex];
    newSteps.splice(draggedIndex, 1);
    newSteps.splice(index, 0, draggedItem);
    setSteps(newSteps);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Touch handlers for mobile devices
  const handleTouchStart = (e, index) => {
    setDraggedIndex(index);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e, index) => {
    if (draggedIndex === null) return;

    e.preventDefault();
    const touchY = e.touches[0].clientY;
    const container = stepsContainerRef.current;
    if (!container) return;

    // Find which element we're hovering over
    const stepElements = container.querySelectorAll('[data-step-index]');
    let targetIndex = null;

    stepElements.forEach((el, idx) => {
      const rect = el.getBoundingClientRect();
      if (touchY >= rect.top && touchY <= rect.bottom) {
        targetIndex = idx;
      }
    });

    if (targetIndex !== null && targetIndex !== draggedIndex) {
      const newSteps = [...steps];
      const draggedItem = newSteps[draggedIndex];
      newSteps.splice(draggedIndex, 1);
      newSteps.splice(targetIndex, 0, draggedItem);
      setSteps(newSteps);
      setDraggedIndex(targetIndex);
    }
  };

  const handleTouchEnd = () => {
    setDraggedIndex(null);
    setTouchStartY(null);
  };

  // Move item up
  const moveUp = (index) => {
    if (index === 0) return;
    const newSteps = [...steps];
    [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
    setSteps(newSteps);
  };

  // Move item down
  const moveDown = (index) => {
    if (index === steps.length - 1) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    setSteps(newSteps);
  };

  const checkOrder = async () => {
    try {
      const response = await axios.post(`${API}/pathway/check`, {
        scenario: selectedScenario,
        order: steps
      });
      setResults(response.data);
    } catch (error) {
      console.error("Error checking pathway:", error);
    }
  };

  const resetGame = () => {
    loadPathway();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              🧩 Arrange the Immune Pathway
            </h3>
            <p className="text-gray-600 mt-1">
              Drag and drop the steps into the correct immunological order!
            </p>
          </div>
          <select
            value={selectedScenario}
            onChange={(e) => setSelectedScenario(e.target.value)}
            data-testid="pathway-scenario-select"
            className="px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all font-medium text-gray-700"
          >
            {scenarios.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Game Area */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
          <h4 className="text-white font-bold">
            📋 {selectedScenario} Pathway Steps
          </h4>
          <p className="text-green-100 text-sm mt-1 hidden md:block">
            Drag steps to reorder them in the correct sequence
          </p>
          <p className="text-green-100 text-sm mt-1 md:hidden">
            Use ▲▼ buttons to reorder steps in the correct sequence
          </p>
        </div>

        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="pathway-steps-container" ref={stepsContainerRef}>
              {steps.map((step, index) => {
                const isCorrect = results?.results?.[index]?.is_correct;
                return (
                  <div
                    key={`${step}-${index}`}
                    data-step-index={index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, index)}
                    onTouchMove={(e) => handleTouchMove(e, index)}
                    onTouchEnd={handleTouchEnd}
                    data-testid={`pathway-step-${index}`}
                    className={`p-3 md:p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-2 md:gap-4 touch-none ${draggedIndex === index ? "scale-105 shadow-lg bg-green-50 border-green-400 z-10" :
                      results && isCorrect === true ? "bg-green-50 border-green-400" :
                        results && isCorrect === false ? "bg-red-50 border-red-400" :
                          "bg-gray-50 border-gray-200 hover:border-green-300 hover:bg-green-50/50"
                      }`}
                  >
                    {/* Up/Down buttons for mobile */}
                    <div className="flex flex-col gap-1 md:hidden">
                      <button
                        onClick={(e) => { e.stopPropagation(); moveUp(index); }}
                        disabled={index === 0 || results !== null}
                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 hover:bg-green-200 disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                        aria-label="Move up"
                      >
                        ▲
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveDown(index); }}
                        disabled={index === steps.length - 1 || results !== null}
                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 hover:bg-green-200 disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                        aria-label="Move down"
                      >
                        ▼
                      </button>
                    </div>

                    <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-xs md:text-sm flex-shrink-0 ${results && isCorrect === true ? "bg-green-500 text-white" :
                      results && isCorrect === false ? "bg-red-500 text-white" :
                        "bg-gradient-to-br from-green-500 to-emerald-600 text-white"
                      }`}>
                      {index + 1}
                    </div>
                    <span className={`flex-1 text-sm md:text-base font-medium cursor-grab active:cursor-grabbing ${results && isCorrect === true ? "text-green-800" :
                      results && isCorrect === false ? "text-red-800" :
                        "text-gray-700"
                      }`}>
                      {step}
                    </span>
                    {results && (
                      <span className="text-lg md:text-xl flex-shrink-0">
                        {isCorrect ? "✅" : "❌"}
                      </span>
                    )}
                    {/* Drag handle indicator for desktop */}
                    <div className="hidden md:flex text-gray-400 cursor-grab">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mt-6">
            <button
              onClick={checkOrder}
              disabled={loading}
              data-testid="check-pathway-btn"
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50"
            >
              ✔️ Check Answer
            </button>
            <button
              onClick={resetGame}
              disabled={loading}
              data-testid="shuffle-pathway-btn"
              className="px-6 py-3 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all duration-300 disabled:opacity-50"
            >
              🔀 Shuffle & Reset
            </button>
          </div>

          {/* Results */}
          {results && (
            <div className={`mt-6 p-6 rounded-xl ${results.percentage >= 80 ? "bg-green-100 border-2 border-green-300" :
              results.percentage >= 50 ? "bg-yellow-100 border-2 border-yellow-300" :
                "bg-red-100 border-2 border-red-300"
              }`}>
              <div className="flex items-center gap-4">
                <div className="text-4xl">
                  {results.percentage >= 80 ? "🎉" : results.percentage >= 50 ? "💪" : "📚"}
                </div>
                <div>
                  <h4 className="font-bold text-xl">
                    Score: {results.score}/{results.total} ({results.percentage}%)
                  </h4>
                  <p className="text-gray-600">
                    {results.percentage >= 80 ? "Excellent work! You've mastered this pathway!" :
                      results.percentage >= 50 ? "Good effort! Review the incorrect steps and try again." :
                        "Keep studying! Understanding these pathways takes practice."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Multiple Choice Quiz Component
const MCQQuiz = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [totalAvailable, setTotalAvailable] = useState(0);

  useEffect(() => {
    fetchCategories();
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const loadQuizData = async () => {
      setLoading(true);
      setResults(null);
      setAnswers({});
      try {
        let response;
        if (selectedCategory === "all") {
          response = await axios.get(`${API}/quiz?shuffle=true&count=10`);
          setQuestions(response.data.questions);
        } else {
          response = await axios.get(`${API}/quiz/category/${encodeURIComponent(selectedCategory)}?shuffle=true`);
          setQuestions(response.data.questions);
        }
      } catch (error) {
        console.error("Error loading quiz:", error);
      } finally {
        setLoading(false);
      }
    };
    loadQuizData();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/quiz/categories`);
      setCategories(response.data.categories);
      setTotalAvailable(response.data.total_questions);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const loadQuiz = async () => {
    setLoading(true);
    setResults(null);
    setAnswers({});
    try {
      let response;
      if (selectedCategory === "all") {
        response = await axios.get(`${API}/quiz?shuffle=true&count=10`);
        setQuestions(response.data.questions);
      } else {
        response = await axios.get(`${API}/quiz/category/${encodeURIComponent(selectedCategory)}?shuffle=true`);
        setQuestions(response.data.questions);
      }
    } catch (error) {
      console.error("Error loading quiz:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/quiz/leaderboard`);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

  const handleAnswerChange = (molecule, answer) => {
    setAnswers(prev => ({ ...prev, [molecule]: answer }));
  };

  const submitQuiz = async () => {
    try {
      let response;
      if (selectedCategory === "all") {
        response = await axios.post(`${API}/quiz/check`, { answers });
      } else {
        response = await axios.post(`${API}/quiz/category/check?category_name=${encodeURIComponent(selectedCategory)}`, { answers });
      }
      setResults(response.data);

      // Save score if name is provided
      if (userName.trim()) {
        await axios.post(`${API}/quiz/save-score?user_name=${encodeURIComponent(userName)}&score=${response.data.score}&total=${response.data.total}`);
        fetchLeaderboard();
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
    }
  };

  // Category icon mapping
  const getCategoryIcon = (name) => {
    const icons = {
      "Cytokines & Their Functions": "🧪",
      "Cells & Their Roles": "🔬",
      "Antibodies & Complement": "🛡️",
      "Effector Mechanisms": "⚔️",
      "Pattern Recognition": "🎯",
      "MHC & Antigen Presentation": "📋",
      "Cancer Immunology": "🎗️",
      "Transplantation Immunology": "🏥",
      "Pathogen-Specific Responses": "🦠",
    };
    return icons[name] || "📚";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              🧠 Immunology Quiz
            </h3>
            <p className="text-gray-600 mt-1">
              Test your knowledge across {totalAvailable} questions in {categories.length} categories!
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLeaderboard(!showLeaderboard)}
              data-testid="toggle-leaderboard-btn"
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl shadow hover:shadow-lg transition-all"
            >
              🏆 Leaderboard
            </button>
          </div>
        </div>

        {/* User Name Input */}
        <div className="mt-4">
          <input
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="Enter your name to save score..."
            data-testid="quiz-username-input"
            className="w-full md:w-64 px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Category Selection */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-gray-100">
        <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          📚 Select Quiz Category
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {/* All Categories Option */}
          <button
            onClick={() => setSelectedCategory("all")}
            data-testid="category-all"
            className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${selectedCategory === "all"
              ? "border-purple-500 bg-purple-50 shadow-md"
              : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/50"
              }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎲</span>
              <div>
                <p className="font-semibold text-gray-800">Random Mix</p>
                <p className="text-sm text-gray-500">10 questions from all categories</p>
              </div>
            </div>
          </button>

          {/* Individual Categories */}
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setSelectedCategory(cat.name)}
              data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${selectedCategory === cat.name
                ? "border-purple-500 bg-purple-50 shadow-md"
                : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/50"
                }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{getCategoryIcon(cat.name)}</span>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{cat.name}</p>
                  <p className="text-xs text-gray-500">{cat.question_count} questions</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      {showLeaderboard && (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <h4 className="text-white font-bold">🏆 Top Scores</h4>
          </div>
          <div className="p-4">
            {leaderboard.length > 0 ? (
              <div className="space-y-2">
                {leaderboard.map((entry, index) => (
                  <div key={entry.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${index === 0 ? "bg-yellow-400 text-yellow-900" :
                        index === 1 ? "bg-gray-300 text-gray-700" :
                          index === 2 ? "bg-orange-300 text-orange-800" :
                            "bg-gray-200 text-gray-600"
                        }`}>
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-700">{entry.user_name}</span>
                    </div>
                    <span className="font-bold text-purple-600">{entry.percentage}%</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No scores yet. Be the first!</p>
            )}
          </div>
        </div>
      )}

      {/* Quiz Questions */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
          <h4 className="text-white font-bold">
            📝 {selectedCategory === "all" ? "Random Quiz" : selectedCategory}
          </h4>
          <p className="text-purple-100 text-sm mt-1">
            {questions.length} questions • Select the correct function for each item
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="quiz-questions-container">
              {questions.map((q, qIndex) => {
                const result = results?.results?.find(r => r.molecule === q.molecule);
                return (
                  <div
                    key={q.molecule}
                    data-testid={`quiz-question-${qIndex}`}
                    className={`p-5 rounded-xl border-2 transition-all ${result && result.is_correct ? "bg-green-50 border-green-300" :
                      result && !result.is_correct ? "bg-red-50 border-red-300" :
                        "bg-gray-50 border-gray-200"
                      }`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-bold rounded-lg">
                        Q{qIndex + 1}
                      </span>
                      <h5 className="font-bold text-gray-800 text-lg">
                        What is the primary function of <span className="text-purple-600">{q.molecule}</span>?
                      </h5>
                      {result && (
                        <span className="ml-auto text-xl">
                          {result.is_correct ? "✅" : "❌"}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {q.options.map((option, oIndex) => {
                        const isSelected = answers[q.molecule] === option;
                        const isCorrect = result && option === result.correct;
                        const isWrong = result && isSelected && !result.is_correct;
                        return (
                          <label
                            key={option}
                            data-testid={`quiz-option-${qIndex}-${oIndex}`}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${isCorrect ? "bg-green-100 border-green-400" :
                              isWrong ? "bg-red-100 border-red-400" :
                                isSelected ? "bg-purple-100 border-purple-400" :
                                  "bg-white border-gray-200 hover:border-purple-300"
                              }`}
                          >
                            <input
                              type="radio"
                              name={q.molecule}
                              value={option}
                              checked={isSelected}
                              onChange={() => handleAnswerChange(q.molecule, option)}
                              disabled={results !== null}
                              className="w-4 h-4 text-purple-600"
                            />
                            <span className={`font-medium ${isCorrect ? "text-green-800" :
                              isWrong ? "text-red-800" :
                                "text-gray-700"
                              }`}>
                              {option}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {result && !result.is_correct && (
                      <p className="mt-3 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                        💡 Correct answer: <strong>{result.correct}</strong>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mt-6">
            <button
              onClick={submitQuiz}
              disabled={loading || results !== null || Object.keys(answers).length === 0}
              data-testid="submit-quiz-btn"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ✔️ Submit Quiz
            </button>
            <button
              onClick={loadQuiz}
              disabled={loading}
              data-testid="reset-quiz-btn"
              className="px-6 py-3 bg-white text-gray-700 font-bold rounded-xl border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-300 disabled:opacity-50"
            >
              🔄 New Quiz
            </button>
          </div>

          {/* Results */}
          {results && (
            <div className={`mt-6 p-6 rounded-xl ${results.percentage >= 80 ? "bg-green-100 border-2 border-green-300" :
              results.percentage >= 50 ? "bg-yellow-100 border-2 border-yellow-300" :
                "bg-red-100 border-2 border-red-300"
              }`}>
              <div className="flex items-center gap-4">
                <div className="text-4xl">
                  {results.percentage >= 80 ? "🎉" : results.percentage >= 50 ? "💪" : "📚"}
                </div>
                <div>
                  <h4 className="font-bold text-xl" data-testid="quiz-score">
                    Score: {results.score}/{results.total} ({results.percentage}%)
                  </h4>
                  <p className="text-gray-600">
                    {results.percentage >= 80 ? "Outstanding! You really know your immunology!" :
                      results.percentage >= 50 ? "Good job! Review the incorrect answers to improve." :
                        "Keep studying! These concepts will become clearer with practice."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Information/About Section
const AboutSection = () => (
  <div className="space-y-6">
    {/* Creator Card */}
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 border border-gray-100">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <img
          src="https://customer-assets.emergentagent.com/job_12a5beb3-0cde-4208-a5d4-88adad24261a/artifacts/slopxdy4_CBTD%20LOGO.JPG"
          alt="CBTD Logo"
          className="w-32 h-32 rounded-2xl bg-white p-2 object-contain shadow-lg border-2 border-indigo-100"
        />
        <div className="text-center md:text-left">
          <h3 className="text-2xl font-bold text-gray-800 mb-2">
            👨‍🔬 Prof. Chatchai Tayapiwatana
          </h3>
          <p className="text-indigo-600 font-semibold text-lg">
            Center of Biomolecular Therapy and Diagnostics (CBTD)
          </p>
          <p className="text-gray-600">
            Chiang Mai University, Thailand
          </p>
          <a
            href="https://research.ams.cmu.ac.th/site/article?id=211"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <span>🔬</span>
            Visit Research Page
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </div>

    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
        <span className="text-3xl">📖</span>
        About This Learning Tool
      </h3>

      <div className="prose prose-lg max-w-none">
        <p className="text-gray-600 leading-relaxed">
          Welcome to the <strong className="text-indigo-600">Interactive Immune System Mindmap</strong>!
          This educational tool is designed to help undergraduate medical technology students
          understand the complex interactions of the immune system.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-100">
            <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
              <span>🧬</span> Mindmap Viewer
            </h4>
            <p className="text-gray-600 text-sm">
              Explore the immune system as an interactive network graph. Select different
              scenarios (viral infections, cancer, transplantation, etc.) to see how the
              immune response differs. Filter by component types and hover over nodes for details.
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100">
            <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span>🧩</span> Pathway Arrangement
            </h4>
            <p className="text-gray-600 text-sm">
              Test your understanding by arranging immune pathway steps in the correct order.
              This helps reinforce the sequential nature of immune responses and the cause-effect
              relationships between different components.
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
            <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
              <span>🧠</span> Quiz Challenge
            </h4>
            <p className="text-gray-600 text-sm">
              Match immune molecules (cytokines, antibodies, etc.) to their primary functions.
              Compete with classmates on the leaderboard and track your progress!
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl border border-amber-100">
            <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
              <span>💡</span> Study Tips
            </h4>
            <p className="text-gray-600 text-sm">
              Work in pairs or small groups! Discuss why certain pathways activate and
              how different scenarios lead to different immune responses. Teaching others
              is one of the best ways to learn.
            </p>
          </div>
        </div>

        <div className="mt-8 p-6 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-white">
          <h4 className="font-bold text-xl mb-3">🎯 Supported Immune Scenarios</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              "🦠 Extracellular bacteria",
              "🔬 Intracellular bacteria",
              "🦠 Viruses",
              "🍄 Fungi",
              "🔴 Protozoa parasites",
              "🐛 Helminths/worms",
              "🏥 Transplantation",
              "🎗️ Cancer"
            ].map((scenario, i) => (
              <div key={i} className="bg-white/20 px-3 py-2 rounded-lg text-sm font-medium">
                {scenario}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Main App Component
function App() {
  const [activeTab, setActiveTab] = useState("mindmap");
  const [totalVisits, setTotalVisits] = useState(0);

  /* Debug log for dashboard access */
  console.log("Current path:", window.location.pathname);
  const isDashboard = window.location.pathname.includes('/secret-dashboard');

  useEffect(() => {
    const logVisit = async () => {
      try {
        const response = await axios.post(`${API}/visit?path=${window.location.pathname}`);
        if (response.data?.success) {
          setTotalVisits(response.data.total_visits);
        }
      } catch (error) {
        console.error("Failed to log visit:", error);
      }
    };
    logVisit();
  }, []);

  if (isDashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
        <VisitorStats />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50 to-purple-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* CBTD Logo */}
              <img
                src="https://customer-assets.emergentagent.com/job_12a5beb3-0cde-4208-a5d4-88adad24261a/artifacts/slopxdy4_CBTD%20LOGO.JPG"
                alt="CBTD Logo"
                className="w-20 h-20 md:w-24 md:h-24 rounded-xl shadow-lg bg-white p-1 object-contain"
              />
              <div className="text-white">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold">
                  Immune System Mindmap
                </h1>
                <p className="text-indigo-100 mt-1 text-sm md:text-base">
                  Interactive Learning Tool for Medical Technology Students
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-xl">
              <span className="text-2xl">🎓</span>
              <span className="text-white font-medium">Medical Education</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-3 justify-center">
            <TabButton
              active={activeTab === "mindmap"}
              onClick={() => setActiveTab("mindmap")}
              icon={<span>🧬</span>}
            >
              Mindmap
            </TabButton>
            <TabButton
              active={activeTab === "pathway"}
              onClick={() => setActiveTab("pathway")}
              icon={<span>🧩</span>}
            >
              Pathway Game
            </TabButton>
            <TabButton
              active={activeTab === "quiz"}
              onClick={() => setActiveTab("quiz")}
              icon={<span>🧠</span>}
            >
              Quiz
            </TabButton>
            <TabButton
              active={activeTab === "about"}
              onClick={() => setActiveTab("about")}
              icon={<span>📖</span>}
            >
              About
            </TabButton>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === "mindmap" && <MindmapViewer />}
        {activeTab === "pathway" && <PathwayGame />}
        {activeTab === "quiz" && <MCQQuiz />}
        {activeTab === "about" && <AboutSection />}
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Creator Section */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-6 pb-6 border-b border-white/20">
            <img
              src="https://customer-assets.emergentagent.com/job_12a5beb3-0cde-4208-a5d4-88adad24261a/artifacts/slopxdy4_CBTD%20LOGO.JPG"
              alt="CBTD Logo"
              className="w-16 h-16 rounded-lg bg-white p-1 object-contain shadow-lg"
            />
            <div className="text-center md:text-left">
              <p className="font-bold text-lg text-white">
                Created by Prof. Chatchai Tayapiwatana
              </p>
              <p className="text-indigo-200 text-sm">
                Center of Biomolecular Therapy and Diagnostics (CBTD)
              </p>
              <p className="text-indigo-200 text-sm">
                Chiang Mai University, Thailand
              </p>
              <a
                href="https://research.ams.cmu.ac.th/site/article?id=211"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-2 text-indigo-300 hover:text-white transition-colors text-sm"
              >
                <span>🔬</span>
                Visit Research Page
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>

          {/* Copyright Section */}
          <div className="text-center">
            <p className="text-indigo-200">
              Interactive Immune System Mindmap • Designed for Undergraduate Medical Technology Education
            </p>
            <p className="text-indigo-300 text-sm mt-2">
              Learn about immune responses to pathogens, transplantation, and cancer through interactive visualization
            </p>
          </div>

          {/* Prominent Copyright */}
          <div className="mt-8 pt-6 border-t border-white/20">
            <div className="flex flex-col items-center justify-center">
              <p className="text-2xl md:text-3xl font-bold text-white mb-2">
                © {new Date().getFullYear()}
              </p>
              <p className="text-lg md:text-xl font-semibold text-indigo-100">
                Prof. Chatchai Tayapiwatana
              </p>
              <p className="text-indigo-200 text-sm mt-1">
                Center of Biomolecular Therapy and Diagnostics (CBTD)
              </p>
              <p className="text-indigo-200 text-sm">
                Chiang Mai University, Thailand
              </p>
              <p className="text-indigo-300 text-xs mt-3">
                All Rights Reserved
              </p>
              {totalVisits > 0 && (
                <div className="mt-6 flex items-center gap-3 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm border border-white/10 hover:bg-white/20 transition-all duration-300 group">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse group-hover:scale-125 transition-transform"></div>
                  <span className="text-indigo-100 text-xs font-medium tracking-wide">
                    {totalVisits.toLocaleString()} Visitors Explored
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
